/**
 * Turns a flat list of admin-fill fields into the blocks a form should actually render.
 *
 * A government form like the 956 maps to ~90 fields, and shown as one flat column it reads as a
 * wall of inputs. Two things collapse it without hiding anything:
 *
 *  - a set of mutually exclusive checkboxes is one question, so it renders as a single radio group
 *    rather than N checkboxes the filler must reason about ("can I tick two?");
 *  - fields sharing a `Prefix: detail` label belong to one question too ("Client office phone:
 *    country code / area code / number"), so they render under that prefix as a titled block with
 *    the prefix stripped from each control.
 *
 * Both are driven by the label text the template mapping already carries, so a template that does
 * not use the convention simply comes back as a list of standalone fields.
 */

import { exclusiveGroupOf } from "./exclusiveCheckboxes";

const SEPARATOR = ": ";

/** The `Prefix` of a `Prefix: detail` label, or null. Only the first separator counts, so a detail
 * that itself contains a colon stays intact. */
export const labelPrefix = (label) => {
  const text = typeof label === "string" ? label : "";
  const at = text.indexOf(SEPARATOR);
  return at > 0 ? text.slice(0, at).trim() : null;
};

/** The `detail` half, falling back to the whole label when there is no prefix to strip. */
export const labelDetail = (label) => {
  const text = typeof label === "string" ? label : "";
  const at = text.indexOf(SEPARATOR);
  return at > 0 ? text.slice(at + SEPARATOR.length).trim() : text;
};

/** The prefix shared by every label in the set, or null when they do not agree on one. */
const sharedPrefix = (fields) => {
  const first = labelPrefix(fields[0]?.label);
  if (!first) return null;
  return fields.every((field) => labelPrefix(field.label) === first) ? first : null;
};

/**
 * Ordered render blocks. Each is one of:
 *   { kind: "choice",  key, legend, options: [{ field, label }], selectedName }
 *   { kind: "section", key, title, fields: [{ field, label }] }
 *   { kind: "single",  key, field }
 *
 * A block takes the position of its first member, so the form keeps the mapping's own field order.
 */
export const buildFieldBlocks = (fields, values) => {
  const list = fields || [];
  const safeValues = values || {};
  const blocks = [];
  const placed = new Set();

  list.forEach((field) => {
    if (placed.has(field.name)) return;

    const group = exclusiveGroupOf(field);
    if (group) {
      const members = list.filter((other) => exclusiveGroupOf(other) === group);
      members.forEach((member) => placed.add(member.name));
      const prefix = sharedPrefix(members);
      blocks.push({
        kind: "choice",
        key: `choice:${group}`,
        legend: prefix || group,
        options: members.map((member) => ({
          field: member,
          label: prefix ? labelDetail(member.label) : member.label,
        })),
        selectedName: members.find((member) => safeValues[member.name] === "true")?.name || "",
      });
      return;
    }

    // Independent checkboxes group under a heading too (the four agent declarations, the four
    // client ones). They stay checkboxes rather than becoming a choice, which is what tells the
    // filler these are each their own yes/no and not a pick-one set.
    const prefix = labelPrefix(field.label);
    const siblings = prefix
      ? list.filter(
          (other) =>
            !placed.has(other.name) &&
            exclusiveGroupOf(other) === null &&
            labelPrefix(other.label) === prefix
        )
      : [];

    if (siblings.length > 1) {
      siblings.forEach((sibling) => placed.add(sibling.name));
      blocks.push({
        kind: "section",
        key: `section:${prefix}`,
        title: prefix,
        fields: siblings.map((sibling) => ({
          field: sibling,
          label: labelDetail(sibling.label),
        })),
      });
      return;
    }

    placed.add(field.name);
    blocks.push({ kind: "single", key: `single:${field.name}`, field });
  });

  return blocks;
};
